import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Platform } from 'react-native';
import NetInfo from '@react-native-community/netinfo';

if (Platform.OS === 'web') {
  NetInfo.configure({
    reachabilityShouldRun: () => false,
  });
}

export interface NetworkState {
  isConnected: boolean;
  isInternetReachable: boolean | null;
  hasInternetConnection: boolean;
}

const defaultState: NetworkState = {
  isConnected: true,
  isInternetReachable: null,
  hasInternetConnection: true,
};

const NetworkContext = createContext<NetworkState>(defaultState);

const DISCONNECTION_GRACE_PERIOD = 2500;

interface NetworkProviderProps {
  children: ReactNode;
}

export const NetworkProvider: React.FC<NetworkProviderProps> = ({ children }) => {
  const [debouncedState, setDebouncedState] = useState<NetworkState>(defaultState);
  const disconnectionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastConnectedStateRef = useRef<boolean>(true);
  const isInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (Platform.OS === 'web') {
      setDebouncedState({
        isConnected: true,
        isInternetReachable: true,
        hasInternetConnection: true,
      });
      return;
    }

    const unsubscribe = NetInfo.addEventListener((state) => {
      const newIsConnected = state.isConnected ?? false;
      const newIsInternetReachable = state.isInternetReachable;
      const isConnectedNow = newIsConnected &&
        (newIsInternetReachable === null || newIsInternetReachable === true);

      if (isConnectedNow && !lastConnectedStateRef.current) {
        if (__DEV__) {
          console.log('[NETWORK] Connection restored');
        }
        if (disconnectionTimerRef.current) {
          clearTimeout(disconnectionTimerRef.current);
          disconnectionTimerRef.current = null;
        }
        setDebouncedState({
          isConnected: newIsConnected,
          isInternetReachable: newIsInternetReachable,
          hasInternetConnection: isConnectedNow,
        });
        lastConnectedStateRef.current = true;
      } else if (!isConnectedNow && lastConnectedStateRef.current) {
        if (__DEV__) {
          console.log('[NETWORK] Connection lost, starting grace period...');
        }
        if (disconnectionTimerRef.current) {
          clearTimeout(disconnectionTimerRef.current);
        }
        disconnectionTimerRef.current = setTimeout(() => {
          if (__DEV__) {
            console.log('[NETWORK] Grace period expired');
          }
          setDebouncedState({
            isConnected: newIsConnected,
            isInternetReachable: newIsInternetReachable,
            hasInternetConnection: false,
          });
          lastConnectedStateRef.current = false;
          disconnectionTimerRef.current = null;
        }, DISCONNECTION_GRACE_PERIOD);
      } else if (!isConnectedNow && !lastConnectedStateRef.current) {
        setDebouncedState({
          isConnected: newIsConnected,
          isInternetReachable: newIsInternetReachable,
          hasInternetConnection: false,
        });
      }
    });

    NetInfo.fetch().then((initialState) => {
      const initialIsConnected = initialState.isConnected ?? false;
      const initialIsInternetReachable = initialState.isInternetReachable;
      const isInitiallyConnected = initialIsConnected &&
        (initialIsInternetReachable === null || initialIsInternetReachable === true);

      lastConnectedStateRef.current = isInitiallyConnected;
      setDebouncedState({
        isConnected: initialIsConnected,
        isInternetReachable: initialIsInternetReachable,
        hasInternetConnection: isInitiallyConnected,
      });

      if (__DEV__ && !isInitializedRef.current) {
        console.log('[NETWORK] Initial state:', isInitiallyConnected ? 'Connected' : 'Disconnected');
        isInitializedRef.current = true;
      }
    });

    return () => {
      unsubscribe();
      if (disconnectionTimerRef.current) {
        clearTimeout(disconnectionTimerRef.current);
      }
    };
  }, []);

  return (
    <NetworkContext.Provider value={debouncedState}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetworkContext = (): NetworkState => {
  return useContext(NetworkContext);
};
